import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { fmtINR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Trash2, Printer, MessageSquare, FileDown, FileUp, Pencil,
  ArrowRightCircle, Download, BellPlus, PackagePlus
} from "lucide-react";
import { exportToExcel } from "@/lib/excel";
import SmartImport from "@/components/app/SmartImport";
import PoActions, { PoStatusChip } from "@/components/app/PoActions";
import { ReminderBanner, addReminder } from "@/pages/PurchaseReminders";
import { tableToPdf, whatsappSend } from "@/lib/pdf";
import { invoiceMessage } from "@/lib/messages";
import FileUploader from "@/components/app/FileUploader";
import { usePinPrompt } from "@/components/app/PinPrompt";

/**
 * Generic transactions module.
 * types supported: sale, purchase, estimate, proforma, credit_note, debit_note, expense, delivery_challan
 */
const TITLES = {
  sale: "Sales", purchase: "Purchase Bills", estimate: "Estimates / Quotations",
  proforma: "Proforma Sales", credit_note: "Credit Notes / Sales Return",
  debit_note: "Debit Notes / Purchase Return", expense: "Expenses",
  delivery_challan: "Delivery Challan", purchase_order: "Purchase Orders",
};

const OUTGOING = ["sale", "pos", "debit_note"];
const STOCK_TYPES = ["sale", "pos", "purchase", "credit_note", "debit_note"];
const BATCH_TYPES = ["purchase", "purchase_order", "credit_note"];

const ADD_LABELS = {
  purchase_order: "Purchase Order",
  sale: "Sale", purchase: "Purchase", estimate: "Estimate", proforma: "Proforma",
  credit_note: "Credit Note", debit_note: "Debit Note", expense: "Expense",
  delivery_challan: "Challan",
};

const emptyLine = { item_id: null, name: "", qty: 1, unit: "PCS", price: 0, discount: 0, gst_rate: 0, hsn: "", batch_no: "", expiry_date: "" };

const emptyTxn = () => ({
  party_id: null, party_name: "", site: "", date: new Date().toISOString(),
  lines: [{ ...emptyLine }], gst_enabled: false, intra_state: true,
  paid: 0, payment_mode: "cash", payment_ref: "", bank_id: null,
  credit_period_days: 0, vehicle_id: null, vehicle_number: "",
  driver_name: "", km: 0, delivery_address: "", delivery_charges: 0,
  loading_charges: 0, unloading_charges: 0, transport_charges: 0, notes: "",
  warehouse_id: null, bin: "", expected_date: "",
});

export default function TxnModule({ txn_type }) {
  // Support comma-separated types for list view; use first token for create/labels
  const listTypes = txn_type;
  const primaryType = txn_type.split(",")[0];
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const pinPrompt = usePinPrompt();
  const [rows, setRows] = useState([]);
  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [banks, setBanks] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [origLines, setOrigLines] = useState([]);
  const [f, setF] = useState(emptyTxn);
  const isOutgoing = OUTGOING.includes(primaryType);
  const isPO = primaryType === "purchase_order";

  const load = async () => {
    const { data } = await api.get(`/txns?txn_type=${txn_type}${q ? `&q=${encodeURIComponent(q)}` : ""}`);
    setRows(data);
  };
  const loadDeps = async () => {
    const [p, i, v, b, w] = await Promise.all([
      api.get("/parties"), api.get("/items"), api.get("/vehicles"), api.get("/banks"),
      api.get("/warehouses").catch(() => ({ data: [] })),
    ]);
    setParties(p.data); setItems(i.data); setVehicles(v.data); setBanks(b.data); setWarehouses(w.data);
  };
  useEffect(() => { load(); loadDeps(); /* eslint-disable-next-line */ }, [txn_type]);
  useEffect(() => { if (sp.get("new")) { openNew(); } /* eslint-disable-next-line */ }, [sp]);

  const openNew = () => {
    setEditingId(null);
    setOrigLines([]);
    setF({ ...emptyTxn(), payment_mode: isPO ? "credit" : "cash" });
    setOpen(true);
  };

  const openEdit = async (id) => {
    const { data } = await api.get(`/txns/${id}`);
    setF({ ...emptyTxn(), ...data, lines: data.lines?.length ? data.lines : [{ ...emptyLine }] });
    setOrigLines(data.lines || []);
    setEditingId(id);
    setOpen(true);
  };

  const totals = useMemo(() => {
    let subtotal = 0, disc = 0, cgst = 0, sgst = 0, igst = 0;
    for (const l of f.lines) {
      const ls = (+l.qty || 0) * (+l.price || 0);
      const ld = +l.discount || 0;
      subtotal += ls;
      disc += ld;
      if (f.gst_enabled) {
        const taxable = ls - ld;
        const t = (taxable * (+l.gst_rate || 0)) / 100;
        if (f.intra_state) { cgst += t / 2; sgst += t / 2; } else { igst += t; }
      }
    }
    const deliv = +f.delivery_charges || 0;
    const load_ch = +f.loading_charges || 0;
    const unload_ch = +f.unloading_charges || 0;
    const trans_ch = +f.transport_charges || 0;
    const extra = deliv + load_ch + unload_ch + trans_ch;
    const total = subtotal - disc + cgst + sgst + igst + extra;
    return { subtotal, disc, cgst, sgst, igst, deliv, load_ch, unload_ch, trans_ch, extra, total: +total.toFixed(2), balance: +(total - (+f.paid || 0)).toFixed(2) };
  }, [f]);

  const setLine = (i, k, v) => {
    const l = [...f.lines];
    l[i] = { ...l[i], [k]: v };
    // if item selected, populate defaults
    if (k === "item_id" && v) {
      const it = items.find((x) => x._id_str === v);
      if (it) {
        const buy = ["purchase", "purchase_order", "debit_note"].includes(primaryType);
        l[i] = { ...l[i], item_id: v, name: it.name, unit: it.unit, price: buy ? it.purchase_price : it.sale_price, gst_rate: it.gst_rate, hsn: it.hsn };
      }
    }
    setF({ ...f, lines: l });
  };
  const addLine = () => setF({ ...f, lines: [...f.lines, { ...emptyLine }] });
  const rmLine = (i) => setF({ ...f, lines: f.lines.filter((_, x) => x !== i) });

  // Available qty per item for outgoing docs (adds back this doc's original qty when editing)
  const availableFor = (itemId) => {
    const it = items.find((x) => x._id_str === itemId);
    if (!it || it.item_type === "service") return Infinity;
    const loc = f.warehouse_id
      ? (it.locations || []).filter((x) => x.warehouse_id === f.warehouse_id).reduce((s, x) => s + (+x.qty || 0), 0)
      : +it.stock || 0;
    return loc + origLines.filter((l) => l.item_id === itemId).reduce((s, l) => s + (+l.qty || 0), 0);
  };
  const stockIssue = (l) => {
    if (!isOutgoing || !l.item_id) return null;
    const avail = availableFor(l.item_id);
    const need = f.lines.filter((x) => x.item_id === l.item_id).reduce((s, x) => s + (+x.qty || 0), 0);
    if (need <= avail) return null;
    return avail <= 0 ? "No available stock" : `No available stock for this qty — only ${avail} available`;
  };

  const save = async () => {
    if (primaryType !== "expense" && !f.party_id && !f.party_name) { toast.error("Select or type a party"); return; }
    if (!f.lines.length || f.lines.every((l) => !l.name)) { toast.error("Add at least one line"); return; }
    const bad = f.lines.find((l) => stockIssue(l));
    if (bad) { toast.error(`${bad.name}: ${stockIssue(bad)}`); return; }
    await pinPrompt();
    try {
      const payload = { ...f, txn_type: primaryType };
      if (editingId) {
        await api.patch(`/txns/${editingId}`, payload);
        toast.success(`${TITLES[primaryType]} updated`);
      } else {
        const { data } = await api.post("/txns", payload);
        toast.success(`${TITLES[primaryType]} saved · ${data.number}`);
      }
      setOpen(false); load(); loadDeps();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const noteDemand = async (l) => {
    const customer = window.prompt(`Customer asked for "${l.name}" — note it as a purchase reminder.\nCustomer name (optional):`, f.party_name || "");
    if (customer === null) return;
    await addReminder({ item_name: l.name, item_id: l.item_id, qty: +l.qty || 1, customer_name: customer });
  };

  const del = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await pinPrompt();
    try { await api.delete(`/txns/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const convert = async (id) => {
    await pinPrompt();
    try { const { data } = await api.post(`/txns/${id}/convert-to-sale`); toast.success(`Converted → ${data.number}`); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const partySelect = (
    <select className="w-full border border-border rounded-md h-9 px-2 bg-white"
      value={f.party_id || ""}
      onChange={(e) => {
        const p = parties.find((x) => x._id_str === e.target.value);
        setF({ ...f, party_id: p?._id_str || null, party_name: p?.name || "", credit_period_days: p?.credit_period_days || 0 });
      }}
      data-testid="txn-party-select">
      <option value="">— select party —</option>
      {parties.map((p) => <option key={p._id_str} value={p._id_str}>{p.name}</option>)}
    </select>
  );

  const isDelivery = primaryType === "delivery_challan";
  const showVehicle = ["sale", "delivery_challan", "pos", "credit_note"].includes(primaryType);
  const showGst = ["sale", "purchase", "estimate", "proforma", "credit_note", "debit_note"].includes(primaryType);

  const doPdf = () => {
    tableToPdf({
      title: TITLES[primaryType],
      subtitle: `${rows.length} entries`,
      columns: [
        { key: "date", label: "Date", w: 80 },
        { key: "number", label: "Number", w: 100 },
        { key: "party_name", label: "Party", w: 130 },
        { key: "total", label: "Total", num: true, w: 80 },
        { key: "paid", label: "Paid", num: true, w: 70 },
        { key: "balance", label: "Balance", num: true, w: 80 },
      ],
      rows: rows.map((r) => ({ ...r, date: new Date(r.date).toLocaleDateString() })),
      filename: `${primaryType}.pdf`,
    });
  };

  const downloadTemplate = () => {
    const sample = [
      { invoice_no: "INV-1", date: "2026-02-19", party_name: "ACME Traders", gstin: "27ABCDE1234F1Z5", item_name: "Product A", qty: 2, price: 500, discount: 0, gst_rate: 18, hsn: "8471", unit: "PCS", payment_mode: "cash", paid: 1180, notes: "" },
      { invoice_no: "INV-1", date: "2026-02-19", party_name: "ACME Traders", gstin: "27ABCDE1234F1Z5", item_name: "Product B", qty: 1, price: 200, discount: 20, gst_rate: 18, hsn: "8471", unit: "PCS", payment_mode: "cash", paid: 0, notes: "" },
      { invoice_no: "INV-2", date: "2026-02-19", party_name: "Global Suppliers", gstin: "", item_name: "Service C", qty: 1, price: 1000, discount: 0, gst_rate: 0, hsn: "", unit: "PCS", payment_mode: "credit", paid: 0, notes: "sample" },
    ];
    exportToExcel(sample, `${primaryType}-import-template.xlsx`, "Template");
  };

  const canImport = ["sale", "purchase", "credit_note", "debit_note", "expense"].includes(primaryType);
  const whBins = warehouses.find((w) => w._id_str === f.warehouse_id)?.bins || [];
  const showBatch = BATCH_TYPES.includes(primaryType);

  return (
    <div>
      <div className="flex items-end justify-between gap-3 flex-wrap mb-6">
        <div>
          <div className="overline">{primaryType.replace("_", " ")}</div>
          <h1 className="text-3xl font-bold tracking-tight">{TITLES[primaryType]}</h1>
        </div>
        <div className="flex items-center gap-2 no-print">
          <Input placeholder="Search number / party..." value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="w-56" data-testid="txn-search" />
          <Button variant="outline" onClick={load}>Filter</Button>
          <Button variant="outline" onClick={() => exportToExcel(rows.map(({ lines, ...r }) => r), `${primaryType}.xlsx`)} data-testid="txn-export"><FileDown size={14} /> Excel</Button>
          <Button variant="outline" onClick={doPdf} data-testid="txn-pdf"><FileDown size={14} /> PDF</Button>
          <Button variant="outline" onClick={downloadTemplate} data-testid="txn-import-template" title="Download sample Excel template"><Download size={14} /> Template</Button>
          {canImport && <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="txn-import-label"><FileUp size={14} /> Import (any Excel)</Button>}
          <Button className="btn-primary" onClick={openNew} data-testid="txn-add-btn"><Plus size={14} /> Add {ADD_LABELS[primaryType] || primaryType}</Button>
        </div>
      </div>

      {["purchase", "purchase_order"].includes(primaryType) && <ReminderBanner />}
      <div className="bg-white border border-border rounded-lg overflow-x-auto">
        <table className="acc-table w-full">
          <thead>
            <tr>
              <th>Date</th><th>Number</th><th>Party</th><th>{isPO ? "Status" : "Site"}</th>
              {isDelivery && <><th>Vehicle</th><th className="num">KM</th></>}
              <th className="num">Total</th><th className="num">Paid</th><th className="num">Balance</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={isDelivery ? 10 : 8} className="text-center py-6 text-muted-foreground">No entries.</td></tr>}
            {rows.map((r) => (
              <tr key={r._id_str} data-testid={`txn-row-${r._id_str}`}>
                <td>{new Date(r.date).toLocaleDateString()}</td>
                <td className="font-mono">{r.number}</td>
                <td>{r.party_name || "—"}</td>
                <td>{isPO ? <PoStatusChip status={r.po_status} /> : (r.site || "—")}</td>
                {isDelivery && <><td>{r.vehicle_number || "—"}</td><td className="num">{r.km || 0}</td></>}
                <td className="num">{fmtINR(r.total)}</td>
                <td className="num">{fmtINR(r.paid)}</td>
                <td className="num">{fmtINR(r.balance)}</td>
                <td>
                  <div className="flex gap-1">
                    <button className="p-1 hover:bg-slate-100 rounded" onClick={() => window.open(`/print/txn/${r._id_str}`, "_blank")} data-testid={`txn-print-${r._id_str}`}><Printer size={14} /></button>
                    <button className="p-1 hover:bg-slate-100 rounded" onClick={async () => {
                      let firm = null; try { firm = (await api.get("/firms/me")).data; } catch {}
                      const draft = invoiceMessage(r, firm);
                      const body = window.prompt("WhatsApp copy of invoice (edit if needed):", draft);
                      if (body) { const phone = parties.find(p => p._id_str === r.party_id)?.phone; whatsappSend(phone, body); }
                    }} data-testid={`txn-msg-${r._id_str}`}><MessageSquare size={14} /></button>
                    <button className="p-1 hover:bg-slate-100 rounded" onClick={() => openEdit(r._id_str)} data-testid={`txn-edit-${r._id_str}`}><Pencil size={14} /></button>
                    {(primaryType === "estimate" || primaryType === "proforma") && r.status !== "converted" && (
                      <button className="p-1 hover:bg-emerald-50 text-emerald-700 rounded" title="Convert to sale" onClick={() => convert(r._id_str)} data-testid={`txn-convert-${r._id_str}`}><ArrowRightCircle size={14} /></button>
                    )}
                    {isPO && <PoActions row={r} parties={parties} onChange={load} />}
                    <button className="p-1 hover:bg-red-50 text-red-600 rounded" onClick={() => del(r._id_str)} data-testid={`txn-del-${r._id_str}`}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto overflow-x-auto">
          <DialogHeader><DialogTitle>{editingId ? "Edit" : "New"} {TITLES[primaryType]}</DialogTitle></DialogHeader>

          <div className="grid grid-cols-3 gap-3">
            {txn_type !== "expense" && (
              <>
                <div><Label>Party</Label>{partySelect}</div>
                <div><Label>Site (branch)</Label>
                  <select className="w-full border border-border rounded-md h-9 px-2 bg-white" value={f.site || ""} onChange={(e) => setF({ ...f, site: e.target.value })} data-testid="txn-site">
                    <option value="">— none —</option>
                    {(parties.find((p) => p._id_str === f.party_id)?.sites || []).map((s, i) => (
                      <option key={i} value={s.name}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
            <div><Label>Date</Label>
              <Input type="date" value={f.date?.slice(0, 10) || ""} onChange={(e) => setF({ ...f, date: new Date(e.target.value).toISOString() })} />
            </div>
            {(STOCK_TYPES.includes(primaryType) || isPO) && warehouses.length > 0 && (
              <>
                <div><Label>Warehouse</Label>
                  <select className="w-full border border-border rounded-md h-9 px-2 bg-white" value={f.warehouse_id || ""} onChange={(e) => setF({ ...f, warehouse_id: e.target.value || null, bin: "" })} data-testid="txn-warehouse">
                    <option value="">— any / unassigned —</option>
                    {warehouses.map((w) => <option key={w._id_str} value={w._id_str}>{w.name}</option>)}
                  </select>
                </div>
                <div><Label>Bin</Label>
                  <select className="w-full border border-border rounded-md h-9 px-2 bg-white" value={f.bin || ""} onChange={(e) => setF({ ...f, bin: e.target.value })} data-testid="txn-bin">
                    <option value="">— none —</option>
                    {whBins.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
              </>
            )}
            {isPO && <div><Label>Expected delivery</Label><Input type="date" value={f.expected_date?.slice(0, 10) || ""} onChange={(e) => setF({ ...f, expected_date: e.target.value })} data-testid="po-expected-date" /></div>}
          </div>
          {["purchase", "purchase_order"].includes(primaryType) && (
            <div className="mt-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2 flex items-center gap-2"><PackagePlus size={13} /> Type any new item name as free text — it is added to your stock list automatically on save.</div>
          )}

          {isDelivery && (
            <div className="grid grid-cols-4 gap-3 mt-3 border-t border-border pt-3">
              <div><Label>Vehicle</Label>
                <select className="w-full border border-border rounded-md h-9 px-2 bg-white" value={f.vehicle_id || ""} onChange={(e) => {
                  const v = vehicles.find((x) => x._id_str === e.target.value);
                  setF({ ...f, vehicle_id: v?._id_str, vehicle_number: v?.number || "", driver_name: v?.driver_name || "" });
                }} data-testid="dc-vehicle">
                  <option value="">— select —</option>
                  {vehicles.map((v) => <option key={v._id_str} value={v._id_str}>{v.number}</option>)}
                </select>
              </div>
              <div><Label>Driver</Label><Input value={f.driver_name} onChange={(e) => setF({ ...f, driver_name: e.target.value })} /></div>
              <div><Label>KM travelled</Label><Input type="number" value={f.km} onChange={(e) => setF({ ...f, km: +e.target.value })} data-testid="dc-km" /></div>
              <div className="col-span-4"><Label>Delivery Address</Label><Input value={f.delivery_address} onChange={(e) => setF({ ...f, delivery_address: e.target.value })} /></div>
            </div>
          )}

          {showGst && (
            <div className="mt-3 border-t border-border pt-3 flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch checked={f.gst_enabled} onCheckedChange={(v) => setF({ ...f, gst_enabled: v })} data-testid="txn-gst-toggle" />
                <Label>Apply GST</Label>
              </div>
              {f.gst_enabled && (
                <div className="flex items-center gap-2">
                  <Switch checked={f.intra_state} onCheckedChange={(v) => setF({ ...f, intra_state: v })} data-testid="txn-intra-toggle" />
                  <Label>{f.intra_state ? "Intra-state (CGST+SGST)" : "Inter-state (IGST)"}</Label>
                </div>
              )}
            </div>
          )}

          <div className="mt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="overline">Line Items</div>
              <Button size="sm" variant="outline" onClick={addLine} data-testid="txn-add-line"><Plus size={12} /> Add line</Button>
            </div>
            <div className="overflow-x-auto border border-border rounded-md">
              <table className="acc-table w-full">
                <thead><tr><th style={{ minWidth: 200 }}>Item</th><th>Unit</th><th className="num">Qty</th><th className="num">Price</th><th className="num">Disc</th>{f.gst_enabled && <th className="num">GST%</th>}{showBatch && <><th>Batch</th><th>Expiry</th></>}<th className="num">Amt</th><th></th></tr></thead>
                <tbody>
                  {f.lines.map((l, i) => {
                    const amt = ((+l.qty || 0) * (+l.price || 0)) - (+l.discount || 0);
                    const issue = stockIssue(l);
                    return (
                      <tr key={i}>
                        <td>
                          <select className={`w-full border rounded-md h-8 px-2 bg-white ${issue ? "border-red-500" : "border-border"}`} value={l.item_id || ""} onChange={(e) => setLine(i, "item_id", e.target.value)} data-testid={`line-item-${i}`}>
                            <option value="">— free text —</option>
                            {items.map((it) => {
                              const svc = it.item_type === "service";
                              const none = isOutgoing && !svc && availableFor(it._id_str) <= 0;
                              return <option key={it._id_str} value={it._id_str}>{it.name}{isOutgoing && !svc ? ` · stock ${it.stock}` : ""}{none ? " — NO AVAILABLE STOCK" : ""}</option>;
                            })}
                          </select>
                          {!l.item_id && <Input className="mt-1" placeholder="Item name" value={l.name} onChange={(e) => setLine(i, "name", e.target.value)} data-testid={`line-name-${i}`} />}
                          {issue && (
                            <div className="text-[11px] text-red-700 mt-1 flex items-center gap-2" data-testid={`line-stock-error-${i}`}>
                              {issue}
                              <button type="button" className="underline text-violet-700 flex items-center gap-1" onClick={() => noteDemand(l)} data-testid={`line-note-demand-${i}`}><BellPlus size={11} /> Note for purchase</button>
                            </div>
                          )}
                        </td>
                        <td><Input value={l.unit} onChange={(e) => setLine(i, "unit", e.target.value)} className="h-8 w-16" /></td>
                        <td className="num"><Input type="number" value={l.qty} onChange={(e) => setLine(i, "qty", +e.target.value)} className="h-8 w-20 text-right" data-testid={`line-qty-${i}`} /></td>
                        <td className="num"><Input type="number" value={l.price} onChange={(e) => setLine(i, "price", +e.target.value)} className="h-8 w-24 text-right" data-testid={`line-price-${i}`} /></td>
                        <td className="num"><Input type="number" value={l.discount} onChange={(e) => setLine(i, "discount", +e.target.value)} className="h-8 w-20 text-right" /></td>
                        {f.gst_enabled && <td className="num"><Input type="number" value={l.gst_rate} onChange={(e) => setLine(i, "gst_rate", +e.target.value)} className="h-8 w-16 text-right" data-testid={`line-gst-${i}`} /></td>}
                        {showBatch && <>
                          <td><Input value={l.batch_no || ""} onChange={(e) => setLine(i, "batch_no", e.target.value)} className="h-8 w-24" placeholder="Batch" data-testid={`line-batch-${i}`} /></td>
                          <td><Input type="date" value={l.expiry_date || ""} onChange={(e) => setLine(i, "expiry_date", e.target.value)} className="h-8 w-36" data-testid={`line-expiry-${i}`} /></td>
                        </>}
                        <td className="num">{fmtINR(amt)}</td>
                        <td><button className="p-1 text-red-600 hover:bg-red-50 rounded" onClick={() => rmLine(i)}><Trash2 size={14} /></button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals + Payment */}
          <div className="grid grid-cols-2 gap-6 mt-4">
            <div className="border border-border rounded-md p-4 space-y-3">
              <div className="overline">Payment</div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Payment Mode</Label>
                  <select className="w-full border border-border rounded-md h-9 px-2 bg-white" value={f.payment_mode} onChange={(e) => setF({ ...f, payment_mode: e.target.value })} data-testid="txn-payment-mode">
                    <option value="cash">Cash</option>
                    <option value="upi">UPI</option>
                    <option value="bank">Bank Transfer</option>
                    <option value="cheque">Cheque</option>
                    <option value="credit">Credit (unpaid)</option>
                  </select>
                </div>
                <div><Label>Paid</Label><Input type="number" value={f.paid} onChange={(e) => setF({ ...f, paid: +e.target.value })} data-testid="txn-paid" /></div>
                {(f.payment_mode === "bank" || f.payment_mode === "upi") && (
                  <div className="col-span-2"><Label>Bank Account</Label>
                    <select className="w-full border border-border rounded-md h-9 px-2 bg-white" value={f.bank_id || ""} onChange={(e) => setF({ ...f, bank_id: e.target.value })}>
                      <option value="">— select —</option>
                      {banks.map((b) => <option key={b._id_str} value={b._id_str}>{b.name}</option>)}
                    </select>
                  </div>
                )}
                <div><Label>Credit period (days)</Label><Input type="number" value={f.credit_period_days} onChange={(e) => setF({ ...f, credit_period_days: +e.target.value })} /></div>
                <div><Label>Payment Ref</Label><Input value={f.payment_ref} onChange={(e) => setF({ ...f, payment_ref: e.target.value })} /></div>
              </div>
              <div><Label>Notes</Label><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
              {primaryType !== "expense" && (
                <div className="border-t border-border pt-3 mt-3">
                  <div className="overline mb-2">Additional Charges</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs">Loading</Label><Input type="number" value={f.loading_charges} onChange={(e) => setF({ ...f, loading_charges: +e.target.value })} data-testid="txn-loading-charges" /></div>
                    <div><Label className="text-xs">Unloading</Label><Input type="number" value={f.unloading_charges} onChange={(e) => setF({ ...f, unloading_charges: +e.target.value })} data-testid="txn-unloading-charges" /></div>
                    <div><Label className="text-xs">Transport</Label><Input type="number" value={f.transport_charges} onChange={(e) => setF({ ...f, transport_charges: +e.target.value })} data-testid="txn-transport-charges" /></div>
                    <div><Label className="text-xs">Delivery / Freight</Label><Input type="number" value={f.delivery_charges} onChange={(e) => setF({ ...f, delivery_charges: +e.target.value })} data-testid="txn-delivery-charges" /></div>
                  </div>
                </div>
              )}
            </div>
            <div className="border border-border rounded-md p-4 space-y-1 font-mono text-sm">
              <div className="overline mb-1">Summary</div>
              <div className="flex justify-between"><span>Subtotal</span><span>{fmtINR(totals.subtotal)}</span></div>
              <div className="flex justify-between"><span>Discount</span><span>-{fmtINR(totals.disc)}</span></div>
              {f.gst_enabled && f.intra_state && (
                <>
                  <div className="flex justify-between"><span>CGST</span><span>{fmtINR(totals.cgst)}</span></div>
                  <div className="flex justify-between"><span>SGST</span><span>{fmtINR(totals.sgst)}</span></div>
                </>
              )}
              {f.gst_enabled && !f.intra_state && (
                <div className="flex justify-between"><span>IGST</span><span>{fmtINR(totals.igst)}</span></div>
              )}
              {totals.load_ch > 0 && <div className="flex justify-between"><span>Loading</span><span>{fmtINR(totals.load_ch)}</span></div>}
              {totals.unload_ch > 0 && <div className="flex justify-between"><span>Unloading</span><span>{fmtINR(totals.unload_ch)}</span></div>}
              {totals.trans_ch > 0 && <div className="flex justify-between"><span>Transport</span><span>{fmtINR(totals.trans_ch)}</span></div>}
              {totals.deliv > 0 && <div className="flex justify-between"><span>Delivery</span><span>{fmtINR(totals.deliv)}</span></div>}
              <div className="flex justify-between font-bold text-lg text-[#064E3B] border-t border-border pt-2 mt-2"><span>Total</span><span data-testid="txn-total">{fmtINR(totals.total)}</span></div>
              <div className="flex justify-between"><span>Paid</span><span>{fmtINR(f.paid)}</span></div>
              <div className={`flex justify-between font-bold ${totals.balance > 0 ? "text-[#991B1B]" : "text-[#166534]"}`}><span>Balance</span><span>{fmtINR(totals.balance)}</span></div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={save} data-testid="txn-save-btn">Save {editingId ? "changes" : txn_type === "sale" ? "& next" : ""}</Button>
          </div>

          {editingId && (
            <div className="mt-4 border-t border-border pt-4">
              <FileUploader kind={txn_type === "expense" ? "receipt" : "attachment"} linked_type="txn" linked_id={editingId}
                label={txn_type === "expense" ? "Receipt / bill copy" : "Attachments (bills, PODs, photos)"} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {canImport && <SmartImport target="invoices" txnType={primaryType} open={importOpen} onClose={() => setImportOpen(false)} onDone={() => { load(); loadDeps(); }} />}
    </div>
  );
}
